/**
 * Generates tenant-isolated storage key.
 */
export function getCartStorageKey(tenantIdentifier = 'default') {
  const clean = String(tenantIdentifier || 'default')
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_');
  return `asl_cart_${clean}`;
}

export const DEFAULT_CUSTOMER = {
  name: '',
  email: '',
  phone: '',
  company: '',
  street: '',
  zip: '',
  city: '',
  notes: '',
};

export function createEmptyCartState(tenantId = 'default') {
  return {
    tenantId,
    customer: { ...DEFAULT_CUSTOMER },
    items: [],
    subtotalCents: 0,
    taxCents: 0,
    totalCents: 0,
    itemCount: 0,
    importedBomKeys: [],
  };
}

export function normalizeCustomer(customer = {}) {
  return {
    name: String(customer?.name ?? '').trim(),
    email: String(customer?.email ?? '').trim().toLowerCase(),
    phone: String(customer?.phone ?? '').trim(),
    company: String(customer?.company ?? '').trim(),
    street: String(customer?.street ?? '').trim(),
    zip: String(customer?.zip ?? '').trim(),
    city: String(customer?.city ?? '').trim(),
    notes: String(customer?.notes ?? '').trim(),
  };
}

export function computeCartTotals(items = [], vatRate = 0.19) {
  const subtotalCents = items.reduce((acc, item) => {
    const qty = Math.max(1, Number(item.quantity) || 1);
    const unitPrice = Math.max(0, Number(item.unitPriceCents || item.price?.cents || 0));
    return acc + (qty * unitPrice);
  }, 0);

  const itemCount = items.reduce((acc, item) => acc + Math.max(1, Number(item.quantity) || 1), 0);
  const taxCents = Math.round(subtotalCents * vatRate);
  const totalCents = subtotalCents + taxCents;

  return { subtotalCents, taxCents, totalCents, itemCount };
}

/**
 * True when at least one line item has no reliable price (unpriced demo
 * articles). The cart must then never present its money totals as a complete
 * price — only "Auf Anfrage".
 */
export function hasUnknownPriceItems(items = []) {
  return (Array.isArray(items) ? items : []).some((item) => !(Number(item.unitPriceCents) > 0));
}

export function normalizeCartState(value, tenantId = 'default', vatRate = 0.19) {
  if (!value || typeof value !== 'object') {
    return createEmptyCartState(tenantId);
  }

  const items = Array.isArray(value.items)
    ? value.items.map((item) => ({
        id: String(item.id || item.productId || item.sku),
        productId: String(item.productId || item.id || ''),
        sku: String(item.sku || ''),
        name: String(item.name || ''),
        slug: String(item.slug || ''),
        manufacturer: String(item.manufacturer || ''),
        categorySlug: String(item.categorySlug || ''),
        primaryImageUrl: String(item.primaryImageUrl || item.images?.[0]?.url || ''),
        unitPriceCents: Math.max(0, Number(item.unitPriceCents ?? item.price?.cents ?? 0)),
        msrpCents: item.msrpCents !== undefined && item.msrpCents !== null ? Number(item.msrpCents) : null,
        unit: String(item.unit || 'Stück'),
        quantity: Math.max(1, parseInt(item.quantity, 10) || 1),
        ...(item.role ? { role: item.role } : {}),
        ...(item.normCitation || item.norm_citation || item.norm_justification
          ? { normCitation: item.normCitation || item.norm_citation || item.norm_justification }
          : {}),
      }))
    : [];

  const totals = computeCartTotals(items, vatRate);
  const importedBomKeys = Array.isArray(value.importedBomKeys)
    ? [...value.importedBomKeys]
    : [];

  return {
    tenantId: String(value.tenantId || tenantId),
    customer: normalizeCustomer(value.customer),
    items,
    importedBomKeys,
    ...totals,
  };
}

export function loadCartState(storage, tenantIdentifier = 'default', vatRate = 0.19) {
  if (!storage) return createEmptyCartState(tenantIdentifier);
  try {
    const key = getCartStorageKey(tenantIdentifier);
    const raw = storage.getItem(key);
    if (!raw) return createEmptyCartState(tenantIdentifier);
    return normalizeCartState(JSON.parse(raw), tenantIdentifier, vatRate);
  } catch {
    return createEmptyCartState(tenantIdentifier);
  }
}

export function saveCartState(storage, state) {
  if (!storage || !state) return;
  try {
    const key = getCartStorageKey(state.tenantId);
    storage.setItem(key, JSON.stringify(state));
  } catch (err) {
    console.warn('[cart-state] Failed to persist cart state:', err);
  }
}

/**
 * Determines whether a given cart item matches an action or target criteria object.
 */
export function isItemMatch(item, target) {
  if (!item || !target) return false;

  if (typeof target === 'string' || typeof target === 'number') {
    const raw = String(target).trim();
    if (!raw) return false;
    return (
      (item.id != null && String(item.id).trim() === raw) ||
      (item.productId != null && String(item.productId).trim() === raw) ||
      (item.product_id != null && String(item.product_id).trim() === raw) ||
      (item.sku != null && String(item.sku).trim() === raw) ||
      (item.article_number != null && String(item.article_number).trim() === raw) ||
      (item.articleNumber != null && String(item.articleNumber).trim() === raw)
    );
  }

  const payload = target.payload;
  const product = target.product;

  const targetId = target.id ?? (payload && typeof payload === 'object' ? payload.id : undefined) ?? (typeof payload === 'string' ? payload : undefined) ?? product?.id;
  const targetProductId = target.productId ?? target.product_id ?? (payload && typeof payload === 'object' ? (payload.productId ?? payload.product_id) : undefined) ?? product?.productId ?? product?.product_id;
  const targetSku = target.sku ?? (payload && typeof payload === 'object' ? payload.sku : undefined) ?? product?.sku;
  const targetArticleNumber = target.article_number ?? target.articleNumber ?? (payload && typeof payload === 'object' ? (payload.article_number ?? payload.articleNumber) : undefined) ?? product?.article_number ?? product?.articleNumber;

  const itemId = item.id != null ? String(item.id).trim() : null;
  const itemProductId = (item.productId != null ? String(item.productId).trim() : null) || (item.product_id != null ? String(item.product_id).trim() : null);
  const itemSku = item.sku != null ? String(item.sku).trim() : null;
  const itemArticleNumber = (item.article_number != null ? String(item.article_number).trim() : null) || (item.articleNumber != null ? String(item.articleNumber).trim() : null);

  if (targetSku != null && String(targetSku).trim() !== '') {
    const s = String(targetSku).trim();
    if (itemSku === s || itemId === s || itemProductId === s || itemArticleNumber === s) return true;
  }

  if (targetArticleNumber != null && String(targetArticleNumber).trim() !== '') {
    const a = String(targetArticleNumber).trim();
    if (itemArticleNumber === a || itemSku === a || itemId === a || itemProductId === a) return true;
  }

  if (targetProductId != null && String(targetProductId).trim() !== '') {
    const p = String(targetProductId).trim();
    if (itemProductId === p || itemId === p || itemSku === p) return true;
  }

  if (targetId != null && String(targetId).trim() !== '') {
    const tid = String(targetId).trim();
    if (itemId === tid || itemProductId === tid || itemSku === tid || itemArticleNumber === tid) return true;
  }

  return false;
}

export function cartReducer(state, action) {
  if (!state) return createEmptyCartState();
  if (!action || !action.type) return state;

  const vatRate = action.vatRate !== undefined
    ? Number(action.vatRate)
    : (action.payload?.vatRate !== undefined ? Number(action.payload.vatRate) : 0.19);

  switch (action.type) {
    case 'add':
    case 'ADD':
    case 'add_item':
    case 'ADD_ITEM': {
      const product = action.product || action.payload?.product || action.payload;
      if (!product) return state;

      const sku = product.sku || product.article_number || product.articleNumber || product.id || '';
      const rawQty = action.quantity ?? action.payload?.quantity ?? 1;
      const qtyToAdd = Math.max(1, parseInt(rawQty, 10) || 1);
      const unitPriceCents = product.price?.cents !== undefined
        ? Number(product.price.cents)
        : Number(product.unitPriceCents || 0);

      const existingIndex = state.items.findIndex((item) => isItemMatch(item, product));

      let nextItems;
      if (existingIndex >= 0) {
        nextItems = state.items.map((item, idx) =>
          idx === existingIndex
            ? {
                ...item,
                quantity: item.quantity + qtyToAdd,
                name: product.name ? String(product.name) : item.name,
              }
            : item
        );
      } else {
        const newItem = {
          id: String(product.id || sku),
          productId: String(product.productId || product.product_id || product.id || ''),
          sku: String(sku),
          name: String(product.name || 'Artikel'),
          slug: String(product.slug || ''),
          manufacturer: String(product.manufacturer || ''),
          categorySlug: String(product.categorySlug || ''),
          primaryImageUrl: String(product.images?.[0]?.url || product.primaryImageUrl || (Array.isArray(product.images) && product.images[0]) || ''),
          unitPriceCents: Math.max(0, unitPriceCents),
          msrpCents: product.msrp?.cents !== undefined ? product.msrp.cents : (product.msrpCents ?? null),
          unit: String(product.unit || 'Stück'),
          quantity: qtyToAdd,
        };
        nextItems = [...state.items, newItem];
      }

      const totals = computeCartTotals(nextItems, vatRate);
      return { ...state, items: nextItems, ...totals };
    }

    case 'import_verified_bom':
    case 'IMPORT_VERIFIED_BOM':
    case 'import_bom':
    case 'IMPORT_BOM': {
      const incomingItems = Array.isArray(action.items)
        ? action.items
        : Array.isArray(action.payload?.items)
          ? action.payload.items
          : Array.isArray(action.payload)
            ? action.payload
            : [];

      const idempotencyKey =
        action.idempotencyKey ||
        action.idempotency_key ||
        action.payload?.idempotencyKey ||
        action.payload?.idempotency_key;

      // Deduplication check: if idempotency_key is already recorded, return state unchanged
      if (idempotencyKey && Array.isArray(state.importedBomKeys) && state.importedBomKeys.includes(idempotencyKey)) {
        return state;
      }

      if (incomingItems.length === 0) {
        return state;
      }

      const mode = action.mode || action.payload?.mode || 'merge'; // 'merge' | 'replace'
      let nextItems = mode === 'replace' ? [] : [...state.items];

      for (const item of incomingItems) {
        if (!item || typeof item !== 'object') continue;

        const rawQty = item.quantity ?? item.qty ?? 1;
        const qtyToAdd = Math.max(1, parseInt(rawQty, 10) || 1);
        const unitPriceCents = Math.max(
          0,
          Number(
            item.unitPriceCents ??
            item.unit_price_cents ??
            item.unit_price_net_cents ??
            item.price?.cents ??
            0
          )
        );

        const sku = String(item.sku || item.article_number || item.articleNumber || item.id || '');
        const existingIndex = nextItems.findIndex((it) => isItemMatch(it, item));

        if (existingIndex >= 0) {
          nextItems = nextItems.map((it, idx) =>
            idx === existingIndex
              ? {
                  ...it,
                  quantity: it.quantity + qtyToAdd,
                  name: item.name || item.title ? String(item.name || item.title) : it.name,
                  unitPriceCents: unitPriceCents > 0 ? unitPriceCents : it.unitPriceCents,
                  role: it.role || item.role,
                  normCitation: it.normCitation || item.normCitation || item.norm_citation || item.norm_justification,
                }
              : it
          );
        } else {
          nextItems.push({
            id: String(item.id || item.productId || item.product_id || sku),
            productId: String(item.productId || item.product_id || item.id || ''),
            sku: String(sku),
            name: String(item.name || item.title || 'Artikel'),
            slug: String(item.slug || ''),
            manufacturer: String(item.manufacturer || ''),
            categorySlug: String(item.categorySlug || item.category_slug || ''),
            primaryImageUrl: String(
              item.primaryImageUrl ||
              item.primary_image_url ||
              item.images?.[0]?.url ||
              (Array.isArray(item.images) && item.images[0]) ||
              ''
            ),
            unitPriceCents,
            msrpCents:
              item.msrpCents !== undefined && item.msrpCents !== null
                ? Number(item.msrpCents)
                : (item.msrp_cents !== undefined && item.msrp_cents !== null ? Number(item.msrp_cents) : null),
            unit: String(item.unit || 'Stück'),
            quantity: qtyToAdd,
            role: item.role || undefined,
            normCitation: item.normCitation || item.norm_citation || item.norm_justification || undefined,
            norm_citation: item.normCitation || item.norm_citation || item.norm_justification || undefined,
          });
        }
      }

      const totals = computeCartTotals(nextItems, vatRate);
      const nextImportedKeys = idempotencyKey
        ? [...(state.importedBomKeys || []), idempotencyKey]
        : (state.importedBomKeys || []);

      return {
        ...state,
        items: nextItems,
        importedBomKeys: nextImportedKeys,
        ...totals,
      };
    }

    case 'quantity':
    case 'QUANTITY':
    case 'set_quantity':
    case 'SET_QUANTITY':
    case 'update_quantity':
    case 'UPDATE_QUANTITY': {
      const rawQty = action.quantity !== undefined
        ? action.quantity
        : (action.payload?.quantity !== undefined ? action.payload.quantity : undefined);
      const qty = typeof rawQty === 'number' ? Math.floor(rawQty) : parseInt(rawQty, 10);

      let nextItems;
      if (isNaN(qty) || qty <= 0) {
        nextItems = state.items.filter((item) => !isItemMatch(item, action));
      } else {
        nextItems = state.items.map((item) =>
          isItemMatch(item, action)
            ? { ...item, quantity: qty }
            : item
        );
      }
      const totals = computeCartTotals(nextItems, vatRate);
      return { ...state, items: nextItems, ...totals };
    }

    case 'remove':
    case 'REMOVE':
    case 'remove_item':
    case 'REMOVE_ITEM': {
      const nextItems = state.items.filter((item) => !isItemMatch(item, action));
      const totals = computeCartTotals(nextItems, vatRate);
      return { ...state, items: nextItems, ...totals };
    }

    case 'customer':
    case 'CUSTOMER':
    case 'set_customer':
    case 'SET_CUSTOMER':
    case 'update_customer':
    case 'UPDATE_CUSTOMER': {
      const customerData = action.customer || action.payload?.customer || action.payload || {};
      return {
        ...state,
        customer: normalizeCustomer({
          ...state.customer,
          ...customerData,
        }),
      };
    }

    case 'product_display_updated':
    case 'PRODUCT_DISPLAY_UPDATED':
    case 'update_product_display':
    case 'UPDATE_PRODUCT_DISPLAY': {
      const productId = action.productId || action.id || action.sku || action.payload?.productId || action.payload?.id || action.payload?.sku;
      const name = action.name || action.displayName || action.payload?.name || action.payload?.displayName;
      if (!productId || !name) return state;

      const nextItems = state.items.map((item) =>
        isItemMatch(item, productId)
          ? { ...item, name: String(name) }
          : item
      );
      return { ...state, items: nextItems };
    }

    case 'clear':
    case 'CLEAR':
    case 'clear_cart':
    case 'CLEAR_CART': {
      const totals = computeCartTotals([], vatRate);
      return { ...state, items: [], ...totals };
    }

    case 'reset':
    case 'RESET':
    case 'reset_cart':
    case 'RESET_CART': {
      return createEmptyCartState(state.tenantId);
    }

    case 'setTenant':
    case 'SET_TENANT':
    case 'set_tenant': {
      const targetTenantId = action.tenantId || action.payload?.tenantId || state.tenantId;
      const nextState = action.nextState || action.payload?.nextState;
      return nextState
        ? normalizeCartState(nextState, targetTenantId, vatRate)
        : createEmptyCartState(targetTenantId);
    }

    default:
      return state;
  }
}
